import { useState, useEffect, useRef } from 'react'
import { X, Printer, Download, Loader2, Landmark } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatDate, formatDateTime } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Invoice modal — shown when PM/Admin/Consultant/Finance clicks "View Invoice".
 * Displays full invoice with project, milestone, deliverables, and payment info.
 * Printable and downloadable as HTML-to-print.
 */
export default function InvoiceView({ milestoneId, onClose }) {
  const { t } = useTranslation('projectsMisc')
  const { t: tc } = useTranslation('common')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const printRef = useRef(null)
  // Where to pay. Not part of the invoice: Finance and the Admin open it here, and each look is recorded.
  const { user } = useAuth()
  // The same rule as the server (permissions.sees_bank_details): Finance, Admin, Super Admin, Master PM.
  const canSeeBank = user && (user.role === 'Admin' || user.role === 'Finance' || user.is_super_admin
    || (user.role === 'Project Manager' && user.is_primary_pm))
  const [bank, setBank] = useState(null)
  const [bankLoading, setBankLoading] = useState(false)
  const [bankError, setBankError] = useState(null)

  const showBank = () => {
    setBankLoading(true)
    setBankError(null)
    api.get(`/deliverables/invoices/${milestoneId}/bank-details`)
      .then(r => setBank(r.data))
      .catch(e => setBankError(e.response?.data?.detail || 'Could not load bank details'))
      .finally(() => setBankLoading(false))
  }

  useEffect(() => {
    api.get(`/deliverables/invoices/${milestoneId}/detail`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load invoice'))
      .finally(() => setLoading(false))
  }, [milestoneId])

  const printInvoice = () => {
    if (!data) return
    const statusColor = data.status === 'Paid' ? '#166534' : data.status === 'Confirmed' ? '#1e40af' : '#92400e'
    const statusBg = data.status === 'Paid' ? '#dcfce7' : data.status === 'Confirmed' ? '#dbeafe' : '#fef3c7'

    const deliverableRows = data.deliverables.map((d, i) => `
      <tr style="border-bottom:1px solid #e5e7eb;">
        <td style="padding:10px 12px;color:#6b7280;font-size:13px;">${i + 1}</td>
        <td style="padding:10px 12px;font-weight:500;font-size:13px;">${d.title}</td>
        <td style="padding:10px 12px;color:#6b7280;font-size:13px;">${d.submission_date ? formatDate(d.submission_date) : '—'}</td>
        <td style="padding:10px 12px;color:#6b7280;font-size:13px;">${d.reviewed_at ? formatDate(d.reviewed_at) : '—'}</td>
      </tr>
    `).join('')

    // Charges / itemised line items (Visa, Flight, Other…) — mirrors the on-screen Charges
    // table so the printed invoice carries the same breakdown, not just the total.
    const chargeRows = (Array.isArray(data.line_items) ? data.line_items : []).map(li => {
      const sub = li.type === 'reimbursement'
        ? [
            [li.vendor, li.date].filter(Boolean).join(' · '),
            (li.currency && li.amount_original != null) ? `${li.currency} ${Number(li.amount_original).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
            li.ai_extracted ? '(AI, verify)' : '',
          ].filter(Boolean).join(' · ')
        : ''
      return `
      <tr style="border-bottom:1px solid #f3f4f6;">
        <td style="padding:10px 12px;font-size:13px;font-weight:500;color:#111;">
          ${li.label || 'Reimbursement'}
          ${sub ? `<div style="font-size:11px;color:#9ca3af;font-weight:400;margin-top:2px;">${sub}</div>` : ''}
        </td>
        <td style="padding:10px 12px;font-size:13px;color:#6b7280;">${li.type === 'fee' ? 'Consultancy fee' : 'Reimbursement'}</td>
        <td style="padding:10px 12px;font-size:13px;text-align:right;font-weight:600;color:#111;">$${Number(li.amount_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      </tr>`
    }).join('')

    const timelineItems = [
      { label: 'Invoice raised by PM', date: data.invoice_triggered_at, done: data.invoice_triggered },
      { label: 'Confirmed by Consultant', date: data.consultant_confirmed_at, done: data.consultant_invoice_confirmed },
      { label: 'Approved by Admin', date: data.admin_invoice_approved_at, done: data.admin_invoice_approved },
      { label: 'Approved by Project Manager', date: data.pm_invoice_approved_at, done: data.pm_invoice_approved },
      { label: 'Payment processed by Finance', date: null, done: data.status === 'Paid' },
    ].map(item => `
      <tr>
        <td style="padding:8px 0;vertical-align:top;width:28px;">
          <div style="width:16px;height:16px;border-radius:50%;background:${item.done ? '#16a34a' : '#d1d5db'};display:inline-block;margin-top:2px;"></div>
        </td>
        <td style="padding:8px 0;font-size:13px;color:${item.done ? '#111' : '#9ca3af'};">
          ${item.label}
          ${item.date ? `<span style="color:#9ca3af;font-size:12px;margin-left:8px;">${formatDate(item.date)}</span>` : ''}
        </td>
      </tr>
    `).join('')

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${data.invoice_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #111827; }
    @page { margin: 15mm 18mm; size: A4; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <!-- TOP BAR -->
  <div style="background:#1d4ed8;height:6px;width:100%;margin-bottom:28px;border-radius:2px;"></div>

  <!-- HEADER -->
  <table style="width:100%;margin-bottom:28px;">
    <tr>
      <td style="vertical-align:top;">
        <div style="font-size:20px;font-weight:800;color:#1d4ed8;letter-spacing:-0.5px;">3 Pillars Data</div>
        <div style="font-size:11px;color:#6b7280;margin-top:4px;">& Training Centre LLC</div>
        <div style="font-size:11px;color:#6b7280;margin-top:2px;">Sharjah Media City Free Zone, UAE</div>
        <div style="font-size:11px;color:#6b7280;">3pillarsdata@gmail.com</div>
      </td>
      <td style="vertical-align:top;text-align:right;">
        <div style="font-size:32px;font-weight:900;color:#111;letter-spacing:1px;">${t('invoice_heading')}</div>
        <div style="font-size:13px;color:#6b7280;margin-top:6px;">${data.invoice_number}</div>
        <div style="margin-top:8px;display:inline-block;background:${statusBg};color:${statusColor};border-radius:20px;padding:4px 14px;font-size:12px;font-weight:700;">${data.status.toUpperCase()}</div>
      </td>
    </tr>
  </table>

  <!-- DIVIDER -->
  <div style="border-top:2px solid #1d4ed8;margin-bottom:24px;"></div>

  <!-- BILLED BY / TO -->
  <table style="width:100%;margin-bottom:24px;border-collapse:separate;border-spacing:12px 0;">
    <tr>
      <td style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;padding:16px;width:48%;vertical-align:top;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#9ca3af;margin-bottom:8px;">${t('billed_by')}</div>
        <div style="font-size:15px;font-weight:700;margin-bottom:4px;">${data.consultant_name || '—'}</div>
        ${data.consultant_code ? `<div style="font-size:11px;color:#9ca3af;font-family:monospace;margin-bottom:2px;">${data.consultant_code}</div>` : ''}
        ${data.consultant_email ? `<div style="font-size:12px;color:#6b7280;">${data.consultant_email}</div>` : ''}
        ${data.consultant_address ? `<div style="font-size:12px;color:#6b7280;white-space:pre-line;margin-top:2px;">${data.consultant_address}</div>` : ''}
      </td>
      <td style="width:4%;"></td>
      <td style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;padding:16px;width:48%;vertical-align:top;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#9ca3af;margin-bottom:8px;">${t('payable_by')}</div>
        <div style="font-size:15px;font-weight:700;margin-bottom:4px;">3 Pillars Data & Training LLC</div>
        <div style="font-size:12px;color:#6b7280;">3pillarsdata@gmail.com</div>
        <div style="font-size:12px;color:#6b7280;">Sharjah Media City Free Zone, UAE</div>
      </td>
    </tr>
  </table>

  <!-- PROJECT + AMOUNT BLOCK -->
  <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;padding:20px 24px;margin-bottom:24px;">
    <table style="width:100%;">
      <tr>
        <td style="vertical-align:top;">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#3b82f6;margin-bottom:6px;">${t('project')}</div>
          <div style="font-size:15px;font-weight:700;">${data.project_name || '—'}</div>
          ${data.project_number ? `<div style="font-size:11px;color:#6b7280;margin-top:3px;">${data.project_number}</div>` : ''}
          <div style="margin-top:14px;">
            <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#3b82f6;margin-bottom:6px;">${t('milestone')}</div>
            <div style="font-size:14px;font-weight:600;">${data.milestone_title}</div>
            ${data.due_date ? `<div style="font-size:11px;color:#6b7280;margin-top:3px;">Due: ${formatDate(data.due_date)}</div>` : ''}
          </div>
        </td>
        <td style="text-align:right;vertical-align:middle;padding-left:20px;">
          <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#3b82f6;margin-bottom:6px;">${t('total_due')}</div>
          <div style="font-size:36px;font-weight:900;color:#1d4ed8;">$${Number(data.total_usd ?? data.payment_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div style="font-size:12px;color:#6b7280;margin-top:4px;">${t('usd_name')}</div>
        </td>
      </tr>
    </table>
  </div>

  <!-- CHARGES / LINE ITEMS -->
  ${chargeRows ? `
  <div style="margin-bottom:24px;">
    <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin-bottom:10px;">${t('charges')}</div>
    <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
      <thead>
        <tr style="background:#f9fafb;">
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">Item</th>
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">Type</th>
          <th style="padding:10px 12px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">${t('amount_usd')}</th>
        </tr>
      </thead>
      <tbody>
        ${chargeRows}
        <tr style="background:#eff6ff;">
          <td style="padding:10px 12px;font-size:13px;font-weight:800;color:#111;" colspan="2">${t('total')}</td>
          <td style="padding:10px 12px;font-size:13px;text-align:right;font-weight:800;color:#1d4ed8;">$${Number(data.total_usd ?? data.payment_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>
  </div>` : ''}

  <!-- DELIVERABLES -->
  <div style="margin-bottom:24px;">
    <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#374151;margin-bottom:10px;">
      Deliverables Submitted &amp; Approved (${data.deliverables.length})
    </div>
    <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
      <thead>
        <tr style="background:#f9fafb;">
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">#</th>
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">${t('deliverable_title')}</th>
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">${t('submitted')}</th>
          <th style="padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;font-weight:600;">${t('approved_display')}</th>
        </tr>
      </thead>
      <tbody>${deliverableRows || `<tr><td colspan="4" style="padding:16px;text-align:center;color:#9ca3af;font-size:13px;">${t('no_approved_record')}</td></tr>`}</tbody>
    </table>
  </div>

  <!-- INVOICE TIMELINE -->
  <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:18px 20px;margin-bottom:28px;">
    <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b7280;margin-bottom:12px;">${t('invoice_timeline')}</div>
    <table style="width:100%;border-collapse:collapse;">${timelineItems}</table>
  </div>

  ${(data.consultant_invoice_confirmed && (data.invoice_signed_name || data.consultant_name)) ? `
  <!-- CONSULTANT SIGNATURE -->
  <div style="border:1px solid #e5e7eb;border-radius:10px;padding:18px 20px;margin-bottom:28px;">
    <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b7280;margin-bottom:10px;">${t('signed_by_consultant')}</div>
    ${data.invoice_signature_data ? `<img src="${data.invoice_signature_data}" alt="signature" style="max-height:64px;max-width:240px;object-fit:contain;display:block;margin-bottom:4px;" />` : ''}
    <div style="font-size:14px;font-weight:600;color:#1f2937;">${data.invoice_signed_name || data.consultant_name}</div>
    ${data.consultant_confirmed_at ? `<div style="font-size:12px;color:#6b7280;">Signed: ${formatDateTime(data.consultant_confirmed_at)}</div>` : ''}
  </div>` : ''}

  <!-- BOTTOM DIVIDER + FOOTER -->
  <div style="border-top:1px solid #e5e7eb;padding-top:16px;text-align:center;">
    <div style="font-size:11px;color:#9ca3af;">This invoice was generated by ProTrack &nbsp;·&nbsp; ${data.invoice_number} &nbsp;·&nbsp; ${formatDate(new Date())}</div>
    <div style="font-size:10px;color:#d1d5db;margin-top:4px;">3 Pillars Data & Training Centre LLC &nbsp;·&nbsp; Sharjah Media City Free Zone &nbsp;·&nbsp; UAE</div>
  </div>

  <!-- BOTTOM ACCENT BAR -->
  <div style="background:#1d4ed8;height:4px;width:100%;margin-top:20px;border-radius:2px;"></div>
</body>
</html>`

    const w = window.open('', '_blank', 'width=900,height=1100')
    w.document.write(html)
    w.document.close()
    setTimeout(() => { w.focus(); w.print() }, 400)
  }

  const statusColor = (s) => {
    if (s === 'Paid') return 'status-paid'
    if (s === 'Confirmed') return 'status-confirmed'
    return 'status-pending'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Modal toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sticky top-0 z-10">
          <h2 className="font-semibold text-gray-800">{t('invoice_details')}</h2>
          <div className="flex items-center gap-2">
            {data && (
              <>
                <button onClick={printInvoice} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-100">
                  <Printer className="w-3.5 h-3.5" /> Print
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
          )}

          {data && (
            <div ref={printRef}>
              {/* Header */}
              <div className="header flex items-start justify-between pb-5 border-b-2 border-blue-600 mb-6">
                <div>
                  <div className="text-xl font-bold text-blue-700">3 Pillars Data & Training LLC</div>
                  <div className="text-xs text-gray-500 mt-1">Sharjah Media City Free Zone, UAE</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">{t('invoice_heading')}</div>
                  <div className="text-sm text-gray-500 mt-1">{data.invoice_number}</div>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold ${
                    data.status === 'Paid' ? 'bg-green-100 text-green-700' :
                    data.status === 'Confirmed' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {data.status}
                  </span>
                </div>
              </div>

              {/* Parties — the consultant bills 3 Pillars (vendor invoice) */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{t('billed_by')}</p>
                  <p className="font-semibold text-gray-900">{data.consultant_name || '—'}</p>
                  {data.consultant_code && <p className="text-[11px] font-mono text-gray-400 mt-0.5">{data.consultant_code}</p>}
                  {data.consultant_email && <p className="text-xs text-gray-500 mt-0.5">{data.consultant_email}</p>}
                  {data.consultant_address && <p className="text-xs text-gray-500 mt-0.5 whitespace-pre-line" data-private>{data.consultant_address}</p>}
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{t('payable_by')}</p>
                  <p className="font-semibold text-gray-900">3 Pillars Data & Training LLC</p>
                  <p className="text-xs text-gray-500 mt-0.5">3pillarsdata@gmail.com</p>
                  <p className="text-xs text-gray-500 mt-0.5">Sharjah Media City Free Zone, UAE</p>
                </div>
              </div>

              {/* Milestone + Amount */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-blue-600 mb-1">{t('project')}</p>
                    <p className="font-semibold text-gray-900">{data.project_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{data.project_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-blue-600 mb-1">{t('total_due')}</p>
                    <p className="text-3xl font-bold text-blue-700">${Number(data.total_usd ?? data.payment_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="text-xs text-gray-500 mt-0.5">USD</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-[10px] uppercase tracking-widest text-blue-600 mb-1">{t('milestone')}</p>
                  <p className="font-semibold text-gray-900">{data.milestone_title}</p>
                  {data.due_date && <p className="text-xs text-gray-500 mt-0.5">Due: {formatDate(data.due_date)}</p>}
                </div>
              </div>

              {/* Itemised charges — milestone fee and/or reimbursement bills, with a total */}
              {Array.isArray(data.line_items) && data.line_items.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">{t('charges')}</h3>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold rounded-tl-lg">{t('item')}</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">{tc('labels.category')}</th>
                        <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase font-semibold rounded-tr-lg">{t('amount_usd')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.line_items.map((li, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 py-2.5 font-medium text-gray-900">
                            {li.label}
                            {li.type === 'reimbursement' && (
                              <span className="block text-xs text-gray-400 font-normal">
                                {[li.vendor, li.date].filter(Boolean).join(' · ')}
                                {li.currency && li.amount_original != null && ` · ${li.currency} ${Number(li.amount_original).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                {li.ai_extracted ? ' · (AI, verify)' : ''}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-gray-500 capitalize">{li.type === 'fee' ? 'Consultancy fee' : 'Reimbursement'}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-gray-900">${Number(li.amount_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50">
                        <td className="px-3 py-2.5 font-bold text-gray-900" colSpan={2}>{t('total')}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-blue-700">${Number(data.total_usd ?? data.payment_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Deliverables */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Approved Deliverables ({data.deliverables.length})</h3>
                {data.deliverables.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">{t('no_approved_yet')}</p>
                ) : (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold rounded-tl-lg">#</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">{tc('labels.name')}</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold">{t('submitted')}</th>
                        <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase font-semibold rounded-tr-lg">{t('approved_display')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.deliverables.map((d, i) => (
                        <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2.5 text-gray-500">{i + 1}</td>
                          <td className="px-3 py-2.5 font-medium text-gray-900">{d.title}</td>
                          <td className="px-3 py-2.5 text-gray-500">{d.submission_date ? formatDate(d.submission_date) : '—'}</td>
                          <td className="px-3 py-2.5 text-gray-500">{d.reviewed_at ? formatDate(d.reviewed_at) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Timeline */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('invoice_timeline')}</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`w-2 h-2 rounded-full ${data.invoice_triggered ? 'bg-blue-500' : 'bg-gray-300'}`} />
                    <span className="text-gray-600">{t('invoice_triggered_pm')}</span>
                    {data.invoice_triggered_at && <span className="text-gray-400 ml-auto">{formatDateTime(data.invoice_triggered_at)}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`w-2 h-2 rounded-full ${data.consultant_invoice_confirmed ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-gray-600">{t('confirmed_by_consultant')}</span>
                    {data.consultant_confirmed_at && <span className="text-gray-400 ml-auto">{formatDateTime(data.consultant_confirmed_at)}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`w-2 h-2 rounded-full ${data.status === 'Paid' ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-gray-600">{t('payment_processed')}</span>
                  </div>
                </div>
              </div>

              {/* Consultant signature block — the e-signature captured when they confirmed the
                  invoice: image (if drawn/uploaded) on top, typed name below, then signed date/time. */}
              {data.consultant_invoice_confirmed && (data.invoice_signed_name || data.consultant_name) && (
                <div className="border border-gray-200 rounded-xl p-4 mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('signed_by_consultant')}</h3>
                  {data.invoice_signature_data && (
                    <img
                      src={data.invoice_signature_data}
                      alt="signature"
                      className="max-h-16 max-w-[240px] object-contain mb-1"
                    />
                  )}
                  <div className="text-sm font-semibold text-gray-800">{data.invoice_signed_name || data.consultant_name}</div>
                  {data.consultant_confirmed_at && (
                    <div className="text-xs text-gray-500">Signed: {formatDateTime(data.consultant_confirmed_at)}</div>
                  )}
                </div>
              )}

              <div className="text-center text-xs text-gray-400 mt-4">
                Generated by ProTrack · {data.invoice_number} · {formatDate(new Date())}
              </div>
            </div>
          )}

          {/* Where to pay: outside the invoice, so it is never printed or downloaded with it. */}
          {data && canSeeBank && (
            <div className="mt-4 border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold text-gray-800">
                  <Landmark className="w-4 h-4 text-amber-700" /> Bank details for payment
                </div>
                {!bank && (
                  <button onClick={showBank} disabled={bankLoading}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-60">
                    {bankLoading ? 'Loading…' : 'Show bank details'}
                  </button>
                )}
              </div>
              {!bank && !bankError && (
                <p className="text-xs text-gray-500 mt-1">Not on the invoice. Opening them is recorded.</p>
              )}
              {bankError && <p className="text-xs text-red-600 mt-2">{bankError}</p>}
              {bank && !bank.has_banking && (
                <p className="text-xs text-gray-600 mt-2">{bank.consultant} has not entered bank details yet.</p>
              )}
              {bank && bank.has_banking && (
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-700" data-private>
                  {bank.account_holder_name && <p><span className="font-medium">A/C name:</span> {bank.account_holder_name}</p>}
                  {bank.bank_name && <p><span className="font-medium">{t('bank')}</span> {bank.bank_name}</p>}
                  {bank.account_number && <p><span className="font-medium">A/C no:</span> {bank.account_number}</p>}
                  {bank.iban && <p><span className="font-medium">{t('iban')}</span> {bank.iban}</p>}
                  {bank.swift_code && <p><span className="font-medium">{t('swift_bic')}</span> {bank.swift_code}</p>}
                  {bank.currency && <p><span className="font-medium">{t('currency')}</span> {bank.currency}</p>}
                  {bank.country && <p><span className="font-medium">{t('country')}</span> {bank.country}</p>}
                  {bank.bank_address && <p className="col-span-2"><span className="font-medium">{t('bank_address')}</span> {bank.bank_address}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
